import type { AxiosRequestConfig } from 'axios';
import z from 'zod';
import { api } from '../../axios.instance';
import { responseContract } from '../../lib';
import {
  revenueAnalysisAvailableRangeSchema,
  revenueAnalysisWindowSchema,
} from './get-revenue-analysis';

const _GetRevenueAnalysisForecastRequestDtoSchema = z.object({
  /** Last actual month of the window, YYYY-MM. Omitted = newest month with revenue. */
  endMonth: z.string().optional(),
  facilitiesIds: z.array(z.number()).optional(),
});

const forecastPointSchema = z.object({
  month: z.string(),
  value: z.number(),
});

const forecastSeriesSchema = z.object({
  actuals: z.array(forecastPointSchema),
  /** The three projected months following the window. */
  forecast: z.array(forecastPointSchema),
});

const GetRevenueAnalysisForecastResponseDtoSchema = z.object({
  window: revenueAnalysisWindowSchema,
  availableRange: revenueAnalysisAvailableRangeSchema,
  revenue: forecastSeriesSchema,
  /** Same months, same projection method, applied to revenue ÷ average residents. */
  revenuePerResident: forecastSeriesSchema,
});

export type GetRevenueAnalysisForecastRequestDto = z.infer<
  typeof _GetRevenueAnalysisForecastRequestDtoSchema
>;

export type GetRevenueAnalysisForecastResponseDto = z.infer<
  typeof GetRevenueAnalysisForecastResponseDtoSchema
>;

export type RevenueAnalysisForecastSeries = z.infer<
  typeof forecastSeriesSchema
>;

export const getRevenueAnalysisForecast = (
  params: GetRevenueAnalysisForecastRequestDto,
  config?: AxiosRequestConfig<GetRevenueAnalysisForecastRequestDto>
) =>
  api
    .get('facilities/revenue-analysis/forecast', { params, ...config })
    .then(responseContract(GetRevenueAnalysisForecastResponseDtoSchema));
